import React, { useState, useEffect } from 'react';
import { db } from '../firebase'; 
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import { UserPlus, ExternalLink, MessageCircle, Briefcase, Tag, Trash2, Mail } from 'lucide-react';

const ClientCRM = ({ teacherId, user }) => {
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    
    // Olga's requested fields: Socials, Profession, and Categories
    const [newClient, setNewClient] = useState({ 
        name: '', email: '', whatsapp: '', linkedin: '', profile: '', 
        category: 'Will have Trial', notes: '' 
    });

    // 1. Fetch Students - Updated to handle "All" or "Specific" Teacher
    useEffect(() => {
        if (!user) return;
    
        let q;
        // If you are Admin, show ALL leads. If Teacher, show only THEIR leads.
        if (role === 'admin') {
            q = query(collection(db, 'crm_profiles'), orderBy('createdAt', 'desc'));
        } else {
            q = query(
                collection(db, 'crm_profiles'), 
                where('teacherId', '==', user.uid),
                orderBy('createdAt', 'desc')
            );
        }
    
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setClients(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        }, (err) => {
            console.error("Query failed, likely index missing. Falling back...");
            const fallbackQ = (role === 'admin') 
                ? query(collection(db, 'crm_profiles'))
                : query(collection(db, 'crm_profiles'), where('teacherId', '==', user.uid));
            onSnapshot(fallbackQ, (s) => setClients(s.docs.map(d => ({ id: d.id, ...d.data() }))));
        });
    
        return () => unsubscribe();
    }, [user, role]);

    // 2. Save Logic - Fully Connected to Olga's Fields
    const handleAddClient = async (e) => {
        e.preventDefault(); 
        if (!newClient.name) return alert("Please enter a name.");

        try {
            await addDoc(collection(db, 'crm_profiles'), {
                ...newClient,
                teacherId: teacherId || user.uid,
                createdAt: serverTimestamp()
            });
            setIsAdding(false);
            setNewClient({ name: '', email: '', whatsapp: '', linkedin: '', profile: '', category: 'Will have Trial', notes: '' });
        } catch (err) {
            console.error(err);
            alert("Database Error: Check your Firestore rules.");
        }
    };

    const updateField = async (clientId, field, value) => {
        await updateDoc(doc(db, 'crm_profiles', clientId), { [field]: value });
    };

    return (
        <div className="flex h-[750px] bg-slate-950 text-slate-100 rounded-2xl overflow-hidden border border-white/10 font-serif shadow-2xl">
            {/* Sidebar: The Roster */}
            <div className="w-1/3 border-r border-white/5 bg-slate-900/50 p-4 flex flex-col">
                <div className="flex justify-between items-center mb-6 pb-2 border-b border-white/5">
                    <h3 className="text-[10px] font-black uppercase tracking-widest text-yellow-500 italic">Student Roster</h3>
                    <button onClick={() => setIsAdding(true)} className="text-yellow-500 hover:scale-125 transition-all"><UserPlus size={20}/></button>
                </div>
                
                <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                    {clients.map(c => (
                        <button 
                            key={c.id} 
                            onClick={() => setSelectedClient(c)}
                            className={`w-full p-4 text-left rounded-xl border transition-all ${selectedClient?.id === c.id ? 'bg-indigo-600/20 border-indigo-500 shadow-lg shadow-indigo-900/20' : 'bg-white/5 border-transparent hover:border-white/10'}`}
                        >
                            <div className="flex justify-between items-start mb-1">
                                <p className="font-bold text-sm truncate w-32">{c.name}</p>
                                <span className={`text-[7px] px-1.5 py-0.5 rounded font-black uppercase ${c.category === 'Already Student' ? 'bg-green-500 text-black' : 'bg-yellow-500 text-black'}`}>
                                    {c.category === 'Already Student' ? 'Student' : 'Trial'}
                                </span>
                            </div>
                            <p className="text-[9px] opacity-40 uppercase italic truncate">{c.profile || 'Goal not set'}</p>
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Editor */}
            <div className="flex-1 p-8 bg-gradient-to-br from-slate-950 to-slate-900 overflow-y-auto">
                {isAdding ? (
                    <form onSubmit={handleAddClient} className="max-w-md space-y-4 animate-in slide-in-from-top-4">
                        <h2 className="text-xl font-black italic uppercase text-yellow-500 mb-6">Create Student Profile</h2>
                        <input placeholder="Name *" className="w-full bg-white/5 border border-white/10 p-3 rounded-xl text-sm" value={newClient.name} onChange={e => setNewClient({...newClient, name: e.target.value})} required />
                        <textarea placeholder="Profession / Goals..." className="w-full bg-white/5 border border-white/10 p-3 rounded-xl text-sm h-24" value={newClient.profile} onChange={e => setNewClient({...newClient, profile: e.target.value})} />
                        
                        <div className="grid grid-cols-2 gap-4">
                            <select className="bg-slate-800 border border-white/10 p-3 rounded-xl text-xs font-bold text-yellow-500 uppercase" value={newClient.category} onChange={e => setNewClient({...newClient, category: e.target.value})}>
                                <option value="Will have Trial">Будет пробное</option>
                                <option value="Had Trial">Было пробное</option>
                                <option value="Already Student">Уже студент</option>
                            </select>
                            <input placeholder="WhatsApp Number" className="bg-white/5 border border-white/10 p-3 rounded-xl text-sm" value={newClient.whatsapp} onChange={e => setNewClient({...newClient, whatsapp: e.target.value})} />
                        </div>

                        <input placeholder="LinkedIn Profile URL" className="w-full bg-white/5 border border-white/10 p-3 rounded-xl text-sm" value={newClient.linkedin} onChange={e => setNewClient({...newClient, linkedin: e.target.value})} />
                        
                        <div className="flex gap-2 pt-6">
                            <button type="submit" className="bg-yellow-600 text-black px-8 py-3 rounded-xl font-black uppercase text-xs shadow-xl shadow-yellow-900/30 active:scale-95 transition-all">Save to Cloud</button>
                            <button type="button" onClick={() => setIsAdding(false)} className="text-white/30 px-6 py-3 text-xs uppercase font-bold">Discard</button>
                        </div>
                    </form>
                ) : selectedClient ? (
                    <div className="space-y-8 animate-in fade-in duration-500">
                        <header className="border-b border-white/10 pb-6">
                            <h2 className="text-4xl font-black italic uppercase tracking-tighter text-white">{selectedClient.name}</h2>
                            <div className="flex flex-wrap gap-3 mt-4">
                                {selectedClient.linkedin && (
                                    <a href={selectedClient.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-indigo-600/20 px-4 py-2 rounded-full text-[10px] font-bold text-indigo-400 border border-indigo-500/30 hover:bg-indigo-600/40 transition-all">
                                        <ExternalLink size={12}/> LINKEDIN
                                    </a>
                                )}
                                {selectedClient.whatsapp && (
                                    <a href={`https://wa.me/${selectedClient.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-green-600/20 px-4 py-2 rounded-full text-[10px] font-bold text-green-400 border border-green-500/30 hover:bg-green-600/40 transition-all">
                                        <MessageCircle size={12}/> WHATSAPP
                                    </a>
                                )}
                            </div>
                        </header>

                        <div className="grid grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <div className="bg-white/5 p-6 rounded-3xl border border-white/10">
                                    <label className="flex items-center gap-2 mb-4 text-yellow-500 uppercase text-[10px] font-black tracking-widest"><Tag size={14}/> Lifecycle Status</label>
                                    <select 
                                        className="w-full bg-slate-950 border border-white/10 p-3 rounded-xl text-xs text-white outline-none"
                                        value={selectedClient.category}
                                        onChange={(e) => updateField(selectedClient.id, 'category', e.target.value)}
                                    >
                                        <option value="Will have Trial">Будет пробное</option>
                                        <option value="Had Trial">Было пробное</option>
                                        <option value="Already Student">Уже студент</option>
                                    </select>
                                </div>
                                
                                <div className="bg-white/5 p-6 rounded-3xl border border-white/10">
                                    <label className="flex items-center gap-2 mb-4 text-indigo-400 uppercase text-[10px] font-black tracking-widest"><Briefcase size={14}/> Student Profile & Goals</label>
                                    <textarea 
                                        className="w-full bg-transparent border-none focus:ring-0 text-sm h-32 leading-relaxed italic"
                                        defaultValue={selectedClient.profile}
                                        onBlur={(e) => updateField(selectedClient.id, 'profile', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="bg-white/5 p-6 rounded-3xl border border-white/10 relative">
                                <label className="flex items-center gap-2 mb-4 text-pink-500 uppercase text-[10px] font-black tracking-widest italic">Secret Reminders</label>
                                <textarea 
                                    className="w-full bg-transparent border-none focus:ring-0 text-sm h-full placeholder:text-slate-700"
                                    placeholder="Write private notes or reminders here..."
                                    defaultValue={selectedClient.notes}
                                    onBlur={(e) => updateField(selectedClient.id, 'notes', e.target.value)}
                                />
                                <p className="absolute bottom-4 right-6 text-[8px] uppercase text-slate-600 font-bold">Auto-saves on leave</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-800 italic space-y-4">
                        <Briefcase size={64} className="opacity-10" />
                        <p className="uppercase text-[10px] font-black tracking-widest opacity-30">Select a lead to manage</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientCRM;